/**
 * Zelus 挑战页 CheckIn API 客户端（Register / Checkin / GetRecords）
 * baseUrl 来自 section data-checkin-api-base（测试：http://localhost:8787；生产：正式域名，无尾斜杠）
 */
(function (global) {
  'use strict';

  var PATH_REGISTER = '/api/ZelusCheckIn/Register';
  /* YApi：ZelusCheckin；与 Register 同控制器时也可能是 ZelusCheckIn — 主路径按 YApi，404 时回退 */
  var PATH_CHECKIN = '/api/ZelusCheckin/Checkin';
  var PATH_CHECKIN_ALT = '/api/ZelusCheckIn/Checkin';
  var PATH_GET_RECORDS = '/api/ZelusCheckIn/GetRecords';

  function trimSlash(url) {
    return String(url || '').trim().replace(/\/+$/, '');
  }

  function isSuccessCode(code, raw) {
    var n = Number(code);
    /* code 0 为业务失败，前端展示后端 message */
    if (code !== undefined && code !== null && code !== '' && !isNaN(n) && n === 0) return false;
    if (raw && (raw.success === true || raw.Success === true)) return true;
    if (code === undefined || code === null || code === '') return true;
    if (!isNaN(n) && (n === 1 || n === 200)) return true;
    if (String(code).toLowerCase() === 'ok' || String(code).toLowerCase() === 'success') {
      return true;
    }
    return false;
  }

  function parseJsonResponse(res) {
    return res.text().then(function (text) {
      var raw = null;
      if (text) {
        try {
          raw = JSON.parse(text);
        } catch (err) {
          raw = { message: text };
        }
      } else {
        raw = {};
      }
      return { res: res, raw: raw };
    });
  }

  function assertOk(parsed, fallbackMsg) {
    var res = parsed.res;
    var raw = parsed.raw || {};
    if (!res.ok) {
      var httpMsg =
        raw.message || raw.Message || 'Request failed (' + res.status + ').';
      var errHttp = new Error(httpMsg);
      errHttp.status = res.status;
      errHttp.code = raw.code !== undefined ? raw.code : raw.Code;
      errHttp.raw = raw;
      throw errHttp;
    }
    var code = raw.code !== undefined ? raw.code : raw.Code;
    if (!isSuccessCode(code, raw)) {
      var bizMsg = raw.message || raw.Message || fallbackMsg || 'Request failed.';
      var errBiz = new Error(bizMsg);
      errBiz.code = code;
      errBiz.raw = raw;
      throw errBiz;
    }
    return raw;
  }

  function extractData(raw) {
    if (!raw) return undefined;
    return raw.data !== undefined ? raw.data : raw.Data;
  }

  function extractUserId(raw) {
    var data = extractData(raw);
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
      var id = data.userId || data.UserId;
      if (id != null && String(id).trim()) return String(id).trim();
    }
    if (raw.userId || raw.UserId) return String(raw.userId || raw.UserId).trim();
    return '';
  }

  function extractActiveTag(raw) {
    if (!raw) return '';
    var data = extractData(raw);
    var tag =
      raw.activeTag ||
      raw.ActiveTag ||
      raw.activePlan ||
      raw.ActivePlan ||
      (data && typeof data === 'object'
        ? data.activeTag ||
          data.ActiveTag ||
          data.activePlan ||
          data.ActivePlan ||
          data.selection ||
          ''
        : '') ||
      '';
    return tag ? String(tag).trim() : '';
  }

  /* GetRecords：dates 为已打卡日期 string[]（升序）；中间空隙视为未打卡 */
  function extractDates(raw) {
    var data = extractData(raw);
    var list = null;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.dates)) {
      list = data.dates;
    } else if (Array.isArray(raw && raw.dates)) {
      list = raw.dates;
    } else if (Array.isArray(data)) {
      list = data;
    } else if (data && typeof data === 'object') {
      list = data.records || data.checkins || null;
    }
    if (!Array.isArray(list)) return [];
    return list
      .map(function (item) {
        var s = String(item || '').trim();
        return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
      })
      .filter(Boolean)
      .sort();
  }

  /**
   * @param {{ name: string, email: string, baseUrl: string, activeTag?: string }} opts
   * @returns {Promise<{ userId: string, activeTag: string, message?: string, code?: *, raw: * }>}
   */
  function register(opts) {
    var baseUrl = trimSlash(opts && opts.baseUrl);
    var name = String((opts && opts.name) || '').trim();
    var email = String((opts && opts.email) || '').trim();
    var activeTag = String((opts && opts.activeTag) || '').trim();

    if (!baseUrl) {
      return Promise.reject(new Error('Check-in API base URL is not configured.'));
    }
    if (!name) {
      return Promise.reject(new Error('Please enter your first name.'));
    }
    if (!email) {
      return Promise.reject(new Error('Please enter your email.'));
    }

    var body = { Name: name, Email: email };
    if (activeTag) body.ActiveTag = activeTag;

    return fetch(baseUrl + PATH_REGISTER, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(parseJsonResponse)
      .then(function (parsed) {
        var raw = assertOk(parsed, 'Registration failed.');
        return {
          userId: extractUserId(raw),
          activeTag: extractActiveTag(raw) || activeTag,
          message: raw.message || raw.Message,
          code: raw.code !== undefined ? raw.code : raw.Code,
          raw: raw,
        };
      });
  }

  /**
   * @param {{ userId: string, utcTime: string, timezone: string, baseUrl: string }} opts
   */
  function checkin(opts) {
    var baseUrl = trimSlash(opts && opts.baseUrl);
    var userId = String((opts && opts.userId) || '').trim();
    var utcTime = String((opts && opts.utcTime) || '').trim();
    var timezone = String((opts && opts.timezone) || '').trim() || 'UTC';

    if (!baseUrl) {
      return Promise.reject(new Error('Check-in API base URL is not configured.'));
    }
    if (!userId) {
      return Promise.reject(new Error('Missing userId for check-in.'));
    }
    if (!utcTime) {
      return Promise.reject(new Error('Missing utcTime for check-in.'));
    }

    return fetch(baseUrl + PATH_CHECKIN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        userId: userId,
        utcTime: utcTime,
        timezone: timezone,
      }),
    })
      .then(function (res) {
        if (res.status === 404) {
          return fetch(baseUrl + PATH_CHECKIN_ALT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              userId: userId,
              utcTime: utcTime,
              timezone: timezone,
            }),
          });
        }
        return res;
      })
      .then(parseJsonResponse)
      .then(function (parsed) {
        var raw = assertOk(parsed, 'Check-in failed.');
        return {
          message: raw.message || raw.Message,
          code: raw.code !== undefined ? raw.code : raw.Code,
          data: extractData(raw),
          raw: raw,
        };
      });
  }

  /**
   * @param {{ userId: string, baseUrl: string }} opts
   * @returns {Promise<{ dates: string[], activeTag: string, raw: * }>}
   */
  function getRecords(opts) {
    var baseUrl = trimSlash(opts && opts.baseUrl);
    var userId = String((opts && opts.userId) || '').trim();

    if (!baseUrl) {
      return Promise.reject(new Error('Check-in API base URL is not configured.'));
    }
    if (!userId) {
      return Promise.reject(new Error('Missing userId for getRecords.'));
    }

    var url =
      baseUrl +
      PATH_GET_RECORDS +
      '?userId=' +
      encodeURIComponent(userId);

    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(parseJsonResponse)
      .then(function (parsed) {
        var raw = assertOk(parsed, 'Failed to load check-in records.');
        return {
          dates: extractDates(raw),
          activeTag: extractActiveTag(raw),
          message: raw.message || raw.Message,
          code: raw.code !== undefined ? raw.code : raw.Code,
          raw: raw,
        };
      });
  }

  global.ZelusChallengeCheckinApi = {
    register: register,
    checkin: checkin,
    getRecords: getRecords,
    PATH_REGISTER: PATH_REGISTER,
    PATH_CHECKIN: PATH_CHECKIN,
    PATH_GET_RECORDS: PATH_GET_RECORDS,
  };
})(typeof window !== 'undefined' ? window : this);
